import { CursorArrowRaysIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { animated, config, useSpring } from '@react-spring/web'
import { deleteNode } from '@ui/lib/reactFlow/nodeMethods/deleteNode'
import { useEffect, useState } from 'react'
import { MdOutlineOutput } from 'react-icons/md'
import { Handle, NodeProps, Position } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { SliceHealthStatus } from '@ui/__generated__/graphql'
import { NodeTypeEnum, SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { convertBytesToText } from '@ui/lib/utils'
import { NodeOptionButtons } from '@ui/pages/pipelinesWorkflow/components/NodeTypes/NodeOptions'
import { NodeOutputSize } from '@ui/pages/pipelinesWorkflow/components/NodeTypes/NodeOutputSize'
import { StackedOutput } from '@ui/pages/pipelinesWorkflow/components/NodeTypes/StackedOutput'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { useNodeAndEdgeStore } from '@ui/stores'
import clsx from 'clsx'

interface NodeContainerProps {
  nodeIsSelected: boolean
  nodeType: NodeTypeEnum
  node: NodeProps
  onClick: () => void
}

export const NodeContainer = ({ nodeIsSelected, nodeType, node, onClick }: NodeContainerProps) => {
  const { nodes, edges } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
    }))
  )

  const [spring, api] = useSpring(() => ({
    from: { gradientDeg: 1 },
  }))

  const [outputSize, setOutputSize] = useState<number | null>(null)

  const { isDemoMode } = useAuth()

  // Start animation when node is selected
  useEffect(() => {
    if (nodeIsSelected) {
      api.start({
        from: {
          gradientDeg: 0,
        },
        to: {
          gradientDeg: 360,
        },
        loop: true,
        config: {
          duration: 4000,
          ...config.wobbly,
        },
      })
    } else {
      api.stop()
    }
  }, [nodeIsSelected, api])

  useEffect(() => {
    const outputSizeData = node.data.size

    if (outputSizeData) {
      setOutputSize(outputSizeData.span + outputSizeData.log + outputSizeData.metric)
    }
  }, [node.data.size])

  const logoPathBySlug = useLogoPathBySlug([node.data.exportType])

  const sources = nodeType === NodeTypeEnum.FIREHOSE ? ['New Relic', 'OTel'] : []
  const dataTestId = {
    [NodeTypeEnum.FIREHOSE]: `firehose-node`,
    [NodeTypeEnum.CODE]: `code-node-${node.id}`,
    [NodeTypeEnum.EXPORT]: `export-node-${node.id}`,
    [NodeTypeEnum.PLACEHOLDER]: `placeholder-node-${node.id}`,
    [NodeTypeEnum.TRANSFORM]: `transformation-node-${node.id}`,
  }[nodeType]
  const title = {
    [NodeTypeEnum.FIREHOSE]: 'View telemetry data',
    [NodeTypeEnum.CODE]: 'View code',
    [NodeTypeEnum.EXPORT]: 'View export options',
    [NodeTypeEnum.PLACEHOLDER]: 'Add transformation or destination',
    [NodeTypeEnum.TRANSFORM]: 'View transformation',
  }[nodeType]
  const hideNodeOptions =
    node.data.isMinimap || node.data.new || node.data.undeletable || nodeType === NodeTypeEnum.FIREHOSE
  let nodeStatusColorConfig

  if (node.data.status) {
    const { logsStatus, spansStatus, metricsStatus } = node.data.status
    const getStatusColorConfig = (status: SliceHealthStatus) => {
      switch (status) {
        case SliceHealthStatus.Enabled:
          return 'bg-green-500 text-green-500'
        case SliceHealthStatus.Disabled:
          return 'bg-gray-500 text-gray-500'
        case SliceHealthStatus.Failed:
          return 'bg-red-500 text-green-500'
      }
    }

    nodeStatusColorConfig = {
      logs: {
        style: getStatusColorConfig(logsStatus),
        status: logsStatus,
      },
      spans: {
        style: getStatusColorConfig(spansStatus),
        status: spansStatus,
      },

      metrics: {
        style: getStatusColorConfig(metricsStatus),
        status: metricsStatus,
      },
    }
  }

  const nodeContent = (nodeType: NodeTypeEnum) => {
    const nodeHandles = (
      <>
        <Handle
          id={TargetHandleEnum.TARGET_PLACEHOLDER}
          className="invisible"
          type="target"
          position={Position.Left}
          isConnectable={false}
        />
        <Handle
          id={TargetHandleEnum.TARGET_LOGS}
          className="invisible -mt-6"
          type="target"
          position={Position.Left}
          isConnectable={false}
        />
        <Handle
          id={TargetHandleEnum.TARGET_SPANS}
          className="invisible"
          type="target"
          position={Position.Left}
          isConnectable={false}
        />
        <Handle
          id={TargetHandleEnum.TARGET_METRICS}
          className="invisible mt-6"
          type="target"
          position={Position.Left}
          isConnectable={false}
        />

        <Handle
          id={SourceHandleEnum.SOURCE_PLACEHOLDER}
          className="invisible"
          type="source"
          position={Position.Right}
          isConnectable={false}
        />
        <Handle
          id={SourceHandleEnum.SOURCE_LOGS}
          className="invisible -mt-6"
          type="source"
          position={Position.Right}
          isConnectable={false}
        />
        <Handle
          id={SourceHandleEnum.SOURCE_SPANS}
          className="invisible"
          type="source"
          position={Position.Right}
          isConnectable={false}
        />
        <Handle
          id={SourceHandleEnum.SOURCE_METRICS}
          className="invisible mt-6"
          type="source"
          position={Position.Right}
          isConnectable={false}
        />
      </>
    )

    switch (nodeType) {
      case NodeTypeEnum.FIREHOSE:
        const shouldRenderOutputSize = Boolean(outputSize && outputSize > 0)

        return (
          <div className="flex h-full flex-col text-left text-xs font-medium">
            <h2 className="h-4 text-xs font-medium">Sources</h2>

            {nodeHandles}
            {shouldRenderOutputSize && (
              <div className={'mb-3 mt-auto flex items-center self-end text-3xs font-normal text-gray-500'}>
                <MdOutlineOutput className={'h-4 w-4 pr-1'} /> {convertBytesToText(outputSize)}/min output
              </div>
            )}
          </div>
        )
      case NodeTypeEnum.CODE:
        return (
          <>
            <div className="flex flex-col justify-normal text-sm">
              <div className="flex h-4 flex-row items-center">
                <h2 className="text-xs font-medium">Code</h2>
              </div>
              <p className="break-words text-2xs font-normal leading-4 text-gray-500">{node.data.description}</p>

              {node.data.new && <CursorArrowRaysIcon className="absolute bottom-3 right-3 h-6 w-6" />}

              {nodeHandles}
            </div>
          </>
        )
      case NodeTypeEnum.EXPORT:
        return (
          <div className="flex h-full flex-col items-start justify-center text-sm">
            <img
              className="mx-auto mt-4 h-8 w-8"
              src={logoPathBySlug[node.data.exportType]}
              alt={node.data.exportType}
            />
            <p className="w-full break-words text-center text-2xs font-normal leading-4 text-gray-500">
              {node.data.description}
            </p>

            {nodeHandles}
            <div className={'mb-2 mt-auto flex items-center self-end text-3xs font-normal text-gray-500'}>
              <MdOutlineOutput className={'h-4 w-4 pr-1'} /> {convertBytesToText(outputSize)}/min output
            </div>
          </div>
        )
      case NodeTypeEnum.TRANSFORM:
        return (
          <div className="flex h-full flex-col text-left text-xs font-medium">
            <div className="flex h-7 flex-col items-start space-y-0.5">
              <p className="text-[11px] font-medium leading-none">Transformation</p>
              <p className="break-words text-2xs font-normal leading-4 text-gray-500">{node.data.description}</p>
            </div>

            {nodeHandles}
          </div>
        )
      case NodeTypeEnum.PLACEHOLDER:
        return (
          <div className="flex h-full flex-row justify-center text-sm">
            <CursorArrowRaysIcon className="my-auto h-5 w-5 text-gray-800" />
            <p className="my-auto text-xs font-normal">Add step</p>

            {nodeHandles}
          </div>
        )
    }
  }

  return (
    <div className="group relative" data-testid={dataTestId}>
      {nodeIsSelected && (
        <animated.div
          style={{
            backgroundImage: spring.gradientDeg.to(
              (deg) => `linear-gradient(${deg}deg, rgba(0,0,0,0) 0%, rgba(202,56,58,1) 100%)`
            ),
          }}
          className={'absolute inset-0 -z-10 h-[196px] w-[116px] -translate-x-0.5 -translate-y-0.5 rounded-md'}
        />
      )}

      <div
        onClick={onClick}
        className={clsx(
          'h-48 w-28 cursor-pointer rounded-md border border-gray-400 p-2.5 text-left font-bold leading-tight',
          'transition-all duration-150 group-hover:border-primary-500 group-hover:ease-in-out',
          {
            'border-red-500': nodeIsSelected,
            'border-dashed border-slate-300 bg-primary-50': nodeType === NodeTypeEnum.PLACEHOLDER,
            'bg-white': nodeType !== NodeTypeEnum.PLACEHOLDER,
            // 'to-white-400 hover:to-white-500 bg-gradient-to-b from-primary-50 hover:from-primary-75':
            //   nodeType === NodeTypeEnum.FIREHOSE,
          }
        )}
        title={title}>
        {nodeContent(nodeType)}
        {(!hideNodeOptions || (nodeType === NodeTypeEnum.FIREHOSE && !node.data.isMinimap)) && (
          <NodeOptionButtons edges={edges} nodes={nodes} node={node} handleClick={onClick} />
        )}

        {![NodeTypeEnum.PLACEHOLDER, NodeTypeEnum.FIREHOSE, NodeTypeEnum.EXPORT].includes(nodeType) && (
          <NodeOutputSize node={node} edges={edges} />
        )}

        {(!hideNodeOptions || node.data.new) && (
          <div data-testid="deletable-code" className="absolute right-1 top-1 z-20 flex">
            <button
              onClick={(e) => {
                e.stopPropagation()
                deleteNode(node, isDemoMode)
              }}
              className="rounded-md text-black opacity-30 hover:text-red-600 hover:opacity-100">
              <XCircleIcon className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Node Datatype Status */}
        {nodeType !== NodeTypeEnum.PLACEHOLDER && nodeStatusColorConfig && (
          <>
            {/* NOTE: Change top-9 when implementing edge datatypes */}
            <div className="absolute -right-0.5 top-12 flex w-full translate-y-1/2 flex-col">
              <div className="mt-[0.5px] flex flex-row items-center justify-end">
                <p className="mr-1 text-[9px] font-medium text-gray-900">Logs</p>
                <div className={clsx('h-1.5 w-1.5 rounded-full', nodeStatusColorConfig.logs.style)} />
              </div>
              <div className="absoulte mt-3 flex flex-row items-center justify-end">
                <p className="mr-1 mt-[1px] text-[9px] font-medium text-gray-900">Spans</p>
                <div className={clsx('h-1.5 w-1.5 rounded-full', nodeStatusColorConfig.spans.style)} />
              </div>
              {/* Note: Metrics hidden for now */}
              <div className="absoulte mt-3 flex flex-row items-center justify-end opacity-0">
                <p className="mb-[0.5px] mr-1 text-[9px] font-medium text-gray-900">Metrics</p>
                <div className={clsx('h-1.5 w-1.5 rounded-full', nodeStatusColorConfig.metrics.style)} />
              </div>
            </div>

            {/* Node Output*/}
            <div
              className={clsx('absolute left-0 h-5 w-full', {
                'bottom-5': sources.length === 0,
                'bottom-10': sources.length >= 1,
              })}>
              <StackedOutput outputSize={'10 GB'} outputStatus={nodeStatusColorConfig} sources={sources} />
            </div>
          </>
        )}

        {/* Node Output*/}
        {/* Just a place holder will be included in the  node output above in a seperate pr */}
        {nodeType === NodeTypeEnum.FIREHOSE && (
          <div
            className={clsx('absolute left-0 h-5 w-full', {
              'bottom-5': sources.length === 0,
              'bottom-10': sources.length >= 1,
            })}>
            <StackedOutput
              outputSize={'10 GB'}
              outputStatus={{
                logs: {
                  style: 'bg-green-500 text-green-500',
                  status: SliceHealthStatus.Enabled,
                },
                spans: {
                  style: 'bg-green-500 text-green-500',
                  status: SliceHealthStatus.Enabled,
                },
                metrics: {
                  style: 'bg-green-500 text-green-500',
                  status: SliceHealthStatus.Enabled,
                },
              }}
              sources={sources}
            />
          </div>
        )}
      </div>
    </div>
  )
}
